/**
 * Session daemon — lightweight HTTP server that owns all browser sessions.
 *
 * Runs on localhost with an OS-assigned port. Both CLI and MCP delegate
 * browser lifecycle and tool execution to this daemon.
 *
 * @module
 */

import { createServer, type IncomingMessage, type ServerResponse } from 'node:http';
import fs from 'node:fs/promises';
import { readFileSync, unlinkSync } from 'node:fs';
import path from 'node:path';
import os from 'node:os';
import {
  PlaywrightClient,
  BrowserToolExecutor,
  consoleLogger,
  dispatchBrowserToolWithTimeout,
} from '@chatsdet/browser-core';
import { LocalBrowserHost } from '../local-browser/host.js';
import type {
  DaemonState,
  SessionInfo,
  SessionLock,
  CreateSessionRequest,
  SwapContextRequest,
  DaemonResponse,
  ToolResponse,
} from './types.js';
import { USER_PAUSED_ERROR } from './types.js';
import { updateControllerBadge, PASSIVE_BADGE_SCRIPT } from './controller-badge.js';
import {
  discardCapturedEvents,
  drainAndEnrichUserEvents,
  isUserDriving,
  recordUserEvents,
  takePendingUserEvents,
  clearUserActivity,
  startActivityTracking,
  stopActivityTracking,
  cleanupActivityTimer,
  injectCaptureScript,
  formatUserActionsSummary,
  waitForActivityPollDrain,
  RESUME_IDLE_MS,
  type TakeoverSession,
} from './user-takeover.js';
import { CdpProxyClient } from './cdp-proxy/index.js';

const PIDFILE_DIR = path.join(os.homedir(), '.config', 'canary-cli');
const PIDFILE_PATH = path.join(PIDFILE_DIR, 'daemon.json');
const IDLE_TIMEOUT_MS = 60_000;

/** Auto-stop sessions that have had no activity for this long (default 1h). */
const SESSION_IDLE_TIMEOUT_MS = (() => {
  const env = process.env.CANARY_SESSION_IDLE_TIMEOUT_MS;
  if (env) {
    const n = Number(env);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 60 * 60 * 1000;
})();
/** How often the idle sweep runs. */
const SESSION_IDLE_SWEEP_INTERVAL_MS = 5 * 60 * 1000;

interface PlaywrightManagedSession extends TakeoverSession {
  kind: 'playwright';
  name: string;
  credentialName?: string;
  startedAt: string;
  lastActivityAt: string;
  lock: SessionLock;
  /** Cloud-relay WebSocket host (when connected to cloud API) */
  relayHost?: LocalBrowserHost;
  /** Cloud session ID (from /local-browser/sessions) */
  localBrowserSessionId?: string;
}

interface AttachedManagedSession {
  kind: 'attached';
  id: string;
  name: string;
  mode: 'attached';
  startedAt: string;
  lastActivityAt: string;
  lock: SessionLock;
  cdpProxy: CdpProxyClient;
}

type ManagedSession = PlaywrightManagedSession | AttachedManagedSession;

function isAttached(s: ManagedSession): s is AttachedManagedSession {
  return s.kind === 'attached';
}

const sessions = new Map<string, ManagedSession>();
let nextId = 1;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let idleSweepTimer: ReturnType<typeof setInterval> | null = null;

/** Mark a session as having just had activity. Called on every action route. */
function touch(s: ManagedSession) {
  s.lastActivityAt = new Date().toISOString();
}

function formatIdle(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function resetIdleTimer() {
  if (idleTimer) clearTimeout(idleTimer);
  if (sessions.size === 0) {
    idleTimer = setTimeout(async () => {
      if (sessions.size === 0) {
        await removePidfile();
        process.exit(0);
      }
    }, IDLE_TIMEOUT_MS);
  }
}

function generateId(): string {
  return `s${nextId++}`;
}

async function getSessionUrl(session: ManagedSession): Promise<string | undefined> {
  if (isAttached(session)) {
    // Real Chrome — we'd have to query via the proxy; skip for now to keep
    // list/status cheap. The user knows what's in their browser.
    return undefined;
  }
  try {
    return await session.client.getCurrentUrl();
  } catch {
    return undefined;
  }
}

async function toSessionInfo(s: ManagedSession): Promise<SessionInfo> {
  if (isAttached(s)) {
    return {
      id: s.id,
      name: s.name,
      mode: 'attached',
      credentialName: undefined,
      url: undefined,
      startedAt: s.startedAt,
      lastActivityAt: s.lastActivityAt,
      lock: s.lock,
    };
  }
  return {
    id: s.id,
    name: s.name,
    mode: s.mode,
    credentialName: s.credentialName,
    url: await getSessionUrl(s),
    startedAt: s.startedAt,
    lastActivityAt: s.lastActivityAt,
    lock: s.lock,
    cloudRelay: s.localBrowserSessionId
      ? { localBrowserSessionId: s.localBrowserSessionId, connected: !!s.relayHost }
      : undefined,
  };
}

/* ── Request parsing helpers ─────────────────────────────────────────── */

async function readBody(req: IncomingMessage): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of req) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks).toString('utf-8');
}

function parseJson(body: string): Record<string, unknown> {
  if (!body.trim()) return {};
  try {
    return JSON.parse(body);
  } catch {
    return {};
  }
}

function json(res: ServerResponse, status: number, data: unknown) {
  const body = JSON.stringify(data);
  res.writeHead(status, {
    'Content-Type': 'application/json',
    'Content-Length': Buffer.byteLength(body),
  });
  res.end(body);
}

function parseRoute(url: string): { path: string[]; query: Record<string, string> } {
  const [pathname] = url.split('?');
  const segments = pathname.split('/').filter(Boolean);
  return { path: segments, query: {} };
}

/* ── Cloud relay ────────────────────────────────────────────────────── */

interface CloudSessionResponse {
  ok: boolean;
  sessionId: string;
  wsToken: string;
  expiresAt: string;
  error?: string;
}

async function setupCloudRelay(
  session: PlaywrightManagedSession,
  apiUrl: string,
  token: string
): Promise<void> {
  // 1. Create cloud session
  const res = await fetch(`${apiUrl}/local-browser/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ browserMode: 'playwright' }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create cloud session: ${res.status} ${text}`);
  }

  const cloudSession = (await res.json()) as CloudSessionResponse;
  if (!cloudSession.ok) {
    throw new Error(`Cloud session creation failed: ${cloudSession.error}`);
  }

  session.localBrowserSessionId = cloudSession.sessionId;

  // 2. Create LocalBrowserHost with the session's existing PlaywrightClient
  const host = new LocalBrowserHost({
    apiUrl,
    wsToken: cloudSession.wsToken,
    sessionId: cloudSession.sessionId,
    browserMode: 'playwright',
    existingClient: session.client,
    skipBrowserLaunch: true,
    shouldExecute: () => session.lock.controller === 'cloud',
    onLog: (level, message, data) => {
      const fn =
        level === 'error'
          ? consoleLogger.error
          : level === 'warn'
            ? consoleLogger.warn
            : consoleLogger.info;
      fn(`[relay:${session.id}] ${message}`, data as Record<string, unknown>);
    },
  });

  await host.start();
  session.relayHost = host;
}

/* ── Route handlers ──────────────────────────────────────────────────── */

async function handleHealth(res: ServerResponse) {
  json(res, 200, { ok: true, sessions: sessions.size });
}

async function handleCreateSession(body: string, res: ServerResponse) {
  const params = parseJson(body) as CreateSessionRequest;
  const id = generateId();
  const name = params.name ?? id;

  if (params.attachRealChrome) {
    // Mutex against options that only make sense for a fresh Playwright session.
    const conflicts: string[] = [];
    if (params.headless) conflicts.push('headless');
    if (params.credentialName) conflicts.push('credential');
    if (params.storageStatePath) conflicts.push('storage-state');
    if (params.url) conflicts.push('url');
    if (params.cloudRelay) conflicts.push('cloud-relay');
    if (conflicts.length > 0) {
      json(res, 400, {
        ok: false,
        error: `--attach-real-chrome cannot be combined with: ${conflicts.join(', ')} (attached mode uses the live browser's profile and tabs).`,
      } satisfies DaemonResponse);
      return;
    }
    for (const existing of sessions.values()) {
      if (existing.kind === 'attached') {
        json(res, 409, {
          ok: false,
          error: `Another attached session is already active ("${existing.id}"). Stop it first with: canary session stop ${existing.id}`,
        } satisfies DaemonResponse);
        return;
      }
    }

    try {
      const cdpProxy = new CdpProxyClient();
      await cdpProxy.start();
      const now = new Date().toISOString();
      const session: AttachedManagedSession = {
        kind: 'attached',
        id,
        name,
        mode: 'attached',
        startedAt: now,
        lastActivityAt: now,
        lock: { controller: 'cli', acquiredAt: now, cloudPaused: false },
        cdpProxy,
      };
      sessions.set(id, session);
      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      json(res, 201, {
        ok: true,
        data: await toSessionInfo(session),
      } satisfies DaemonResponse<SessionInfo>);
    } catch (err) {
      json(res, 500, {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      } satisfies DaemonResponse);
    }
    return;
  }

  const mode = params.headless ? 'headless' : 'headed';

  try {
    const client = new PlaywrightClient({ logger: consoleLogger });
    await client.connect({
      browserMode: mode,
      storageStatePath: params.storageStatePath,
      cursorOverlay: mode === 'headed',
    });

    const executor = new BrowserToolExecutor(client, {
      includeScreenshotWithSnapshot: true,
      clickValidation: true,
      logger: consoleLogger,
    });

    const now = new Date().toISOString();
    const session: PlaywrightManagedSession = {
      kind: 'playwright',
      id,
      name,
      mode,
      credentialName: params.credentialName,
      client,
      executor,
      startedAt: now,
      lastActivityAt: now,
      lock: { controller: 'cli', acquiredAt: now, cloudPaused: false },
      lastUserInputAt: null,
      pendingUserEvents: [],
      activityPollTimer: null,
      activityPollDrain: null,
      toolInFlight: false,
    };

    // Inject interaction capture script and the passive "who's driving" badge
    // for headed sessions. Both use addInitScript so they survive navigations.
    if (mode === 'headed') {
      await injectCaptureScript(client);
      const page = await client.getPageForReplay();
      if (page) {
        try {
          await page.addInitScript(PASSIVE_BADGE_SCRIPT);
        } catch {
          // Best effort — badge is non-critical
        }
      }
      await updateControllerBadge(client, 'cli', { interactive: true });
      // Cooperative control: continuously observe user input so the agent can
      // defer while the user is driving and auto-resume once they go idle.
      startActivityTracking(session);
    }

    sessions.set(id, session);
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }

    // Set up cloud relay before responding so the caller gets the localBrowserSessionId
    if (params.cloudRelay) {
      try {
        await setupCloudRelay(session, params.cloudRelay.apiUrl, params.cloudRelay.token);
      } catch (err) {
        consoleLogger.warn(`Cloud relay setup failed: ${err}`);
        // Continue without relay — session is still usable for CLI control
      }
    }

    const result: DaemonResponse<SessionInfo> = {
      ok: true,
      data: await toSessionInfo(session),
    };
    json(res, 201, result);

    // Navigate after responding — navigation can take 30+ seconds and the
    // CLI intentionally does not wait so callers can issue commands in parallel.
    if (params.url) {
      client.navigate(params.url).catch((err) => {
        consoleLogger.warn(`Initial navigation to ${params.url} failed: ${err}`);
      });
    }
  } catch (err) {
    const result: DaemonResponse = {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
    json(res, 500, result);
  }
}

async function handleListSessions(res: ServerResponse) {
  const list = await Promise.all(Array.from(sessions.values()).map(toSessionInfo));
  json(res, 200, { ok: true, data: list } satisfies DaemonResponse<SessionInfo[]>);
}

async function handleGetSession(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, {
      ok: false,
      error: `Session "${sessionId}" not found`,
    } satisfies DaemonResponse);
    return;
  }
  json(res, 200, {
    ok: true,
    data: await toSessionInfo(session),
  } satisfies DaemonResponse<SessionInfo>);
}

async function teardownSession(session: ManagedSession): Promise<void> {
  if (isAttached(session)) {
    try {
      await session.cdpProxy.shutdown();
    } catch {
      // Best effort
    }
    return;
  }
  cleanupActivityTimer(session);
  if (session.relayHost) {
    try {
      await session.relayHost.stop();
    } catch {
      // Best effort
    }
  }
  try {
    await session.client.disconnect();
  } catch {
    // Best effort
  }
}

/** Tear down and remove a session from the map. Shared by the DELETE route and the idle sweep. */
async function stopSession(
  sessionId: string,
  reason: 'manual' | 'idle' | 'all'
): Promise<boolean> {
  const session = sessions.get(sessionId);
  if (!session) return false;
  if (reason === 'idle') {
    const idleMs = Date.now() - Date.parse(session.lastActivityAt);
    consoleLogger.info(
      `[daemon] auto-stopping session ${session.id} (${session.name}) — idle ${formatIdle(idleMs)}`
    );
  }
  await teardownSession(session);
  sessions.delete(sessionId);
  return true;
}

async function handleDeleteSession(sessionId: string, res: ServerResponse) {
  const stopped = await stopSession(sessionId, 'manual');
  if (!stopped) {
    json(res, 404, {
      ok: false,
      error: `Session "${sessionId}" not found`,
    } satisfies DaemonResponse);
    return;
  }
  json(res, 200, { ok: true } satisfies DaemonResponse);
  resetIdleTimer();
}

async function handleDeleteAllSessions(res: ServerResponse) {
  const ids = Array.from(sessions.keys());
  for (const id of ids) {
    await stopSession(id, 'all');
  }
  json(res, 200, { ok: true } satisfies DaemonResponse);
  resetIdleTimer();
}

/** Sweep idle sessions and auto-stop any past the timeout. */
async function sweepIdleSessions() {
  const now = Date.now();
  const toStop: string[] = [];
  for (const [id, s] of sessions) {
    // Skip cloud-controlled sessions — cloud lifecycle owns them.
    if (s.lock.controller === 'cloud') continue;
    const idleMs = now - Date.parse(s.lastActivityAt);
    if (idleMs >= SESSION_IDLE_TIMEOUT_MS) toStop.push(id);
  }
  for (const id of toStop) {
    try {
      await stopSession(id, 'idle');
    } catch (err) {
      consoleLogger.warn(`[daemon] idle-stop failed for ${id}: ${err}`);
    }
  }
  if (toStop.length > 0) resetIdleTimer();
}

async function handleSwapContext(sessionId: string, body: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, {
      ok: false,
      error: `Session "${sessionId}" not found`,
    } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'swap-context is not supported in attached mode (real Chrome owns the profile).',
    } satisfies DaemonResponse);
    return;
  }

  touch(session);
  const params = parseJson(body) as SwapContextRequest;

  try {
    await session.client.swapContext({
      storageStatePath: params.storageStatePath,
    });

    if (params.credentialName) {
      session.credentialName = params.credentialName;
    }

    json(res, 200, {
      ok: true,
      data: await toSessionInfo(session),
    } satisfies DaemonResponse<SessionInfo>);
  } catch (err) {
    json(res, 500, {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    } satisfies DaemonResponse);
  }
}

async function handleGetStorageState(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, {
      ok: false,
      error: `Session "${sessionId}" not found`,
    } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'storage-state is not supported in attached mode.',
    } satisfies DaemonResponse);
    return;
  }

  try {
    const storageState = await session.client.getStorageState();
    json(res, 200, { ok: true, data: storageState } satisfies DaemonResponse);
  } catch (err) {
    json(res, 500, {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    } satisfies DaemonResponse);
  }
}

async function handleToolCall(
  sessionId: string,
  toolName: string,
  body: string,
  res: ServerResponse
) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, {
      ok: false,
      error: `Session "${sessionId}" not found`,
    } satisfies DaemonResponse);
    return;
  }

  // Enforce session lock — CLI tool calls are rejected when cloud has control
  if (session.lock.controller === 'cloud') {
    json(res, 423, {
      ok: false,
      error: 'Session is controlled by cloud. Use take-control to force CLI control.',
    } satisfies ToolResponse);
    return;
  }

  touch(session);

  // Attached (real Chrome) sessions proxy directly to chrome-devtools-mcp.
  // No pause/takeover flow — the user owns their browser directly.
  if (isAttached(session)) {
    const args = parseJson(body);
    const fullToolName = toolName.startsWith('browser_') ? toolName : `browser_${toolName}`;
    try {
      const result = await session.cdpProxy.callByCanaryName(fullToolName, args);
      if (!result.ok) {
        json(res, 500, { ok: false, error: result.error } satisfies ToolResponse);
        return;
      }
      json(res, 200, {
        ok: true,
        text: result.text,
        images: result.images,
      } satisfies ToolResponse);
    } catch (err) {
      json(res, 500, {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      } satisfies ToolResponse);
    }
    return;
  }

  // Cooperative control: gate the always-on activity poll for the duration of
  // this tool call so the agent's own synthetic events aren't drained as user
  // input, then do a final synchronous drain to catch anything the poll missed.
  session.toolInFlight = true;
  try {
    await waitForActivityPollDrain(session);
    const fresh = await drainAndEnrichUserEvents(session);
    recordUserEvents(session, fresh);

    // If the user has produced input within the resume window, defer this action
    // and hand the agent exactly what the user did. Auto-resumes once they idle.
    if (isUserDriving(session)) {
      const events = takePendingUserEvents(session);
      const summary = formatUserActionsSummary(events, null);
      const idleMs = session.lastUserInputAt ? Date.now() - session.lastUserInputAt : 0;
      json(res, 423, {
        ok: false,
        error:
          `The user is actively interacting with the browser (last input ${idleMs}ms ago). ` +
          `Deferring your action — do not retry immediately; it will run automatically once ` +
          `they are idle for ${RESUME_IDLE_MS}ms.` +
          (summary ? `\n\n${summary}` : ''),
        errorCode: USER_PAUSED_ERROR,
      } satisfies ToolResponse);
      return;
    }

    // User is idle → execute. Prepend any actions they took since the last call.
    const userEvents = takePendingUserEvents(session);

    const args = parseJson(body);
    const fullToolName = toolName.startsWith('browser_') ? toolName : `browser_${toolName}`;

    try {
      const result = await dispatchBrowserToolWithTimeout(session.executor, fullToolName, args);

      // Discard events generated by Claude's own Playwright action so they
      // aren't reported as "user actions" in the next tool call's drain.
      await discardCapturedEvents(session);

      let text: string;
      let images: Array<{ data: string; mimeType: string }> | undefined;

      if (typeof result === 'string') {
        text = result;
      } else {
        text = result.text ?? '';
        // MCPContentResult carries visual output on `media` (browser_scan's
        // context crop); `images` is the legacy field. Accept either.
        images = result.media ?? result.images;
      }

      if (userEvents.length > 0) {
        const summary = formatUserActionsSummary(userEvents, null);
        if (summary) {
          text = summary + '\n' + text;
        }
      }

      json(res, 200, { ok: true, text, images } satisfies ToolResponse);
    } catch (err) {
      await discardCapturedEvents(session);
      json(res, 500, {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      } satisfies ToolResponse);
    }
  } finally {
    session.toolInFlight = false;
  }
}

/* ── Lock management ────────────────────────────────────────────────── */

async function handleGetLock(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, { ok: false, error: `Session "${sessionId}" not found` } satisfies DaemonResponse);
    return;
  }
  json(res, 200, { ok: true, data: session.lock } satisfies DaemonResponse<SessionLock>);
}

async function handleTakeControl(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, { ok: false, error: `Session "${sessionId}" not found` } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'Cloud lock operations are not supported in attached mode.',
    } satisfies DaemonResponse);
    return;
  }

  touch(session);
  const wasClouded = session.lock.controller === 'cloud';
  session.lock = {
    controller: 'cli',
    acquiredAt: new Date().toISOString(),
    cloudPaused: wasClouded,
  };

  // Hard escape hatch: force the agent back into control by clearing any
  // in-progress user takeover, so a stuck "user is driving" state can't wedge
  // the session. For headed sessions, re-arm the passive activity poll + badge.
  clearUserActivity(session);
  if (session.mode === 'headed') {
    startActivityTracking(session);
    await updateControllerBadge(session.client, 'cli', { interactive: true });
  } else {
    await updateControllerBadge(session.client, 'cli');
  }
  json(res, 200, { ok: true, data: await toSessionInfo(session) } satisfies DaemonResponse<SessionInfo>);
}

async function handleResumeCloud(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, { ok: false, error: `Session "${sessionId}" not found` } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'Cloud lock operations are not supported in attached mode.',
    } satisfies DaemonResponse);
    return;
  }

  if (!session.lock.cloudPaused) {
    json(res, 409, { ok: false, error: 'Cloud is not paused' } satisfies DaemonResponse);
    return;
  }

  touch(session);
  session.lock = {
    controller: 'cloud',
    acquiredAt: new Date().toISOString(),
    cloudPaused: false,
  };

  // Cloud is driving now — no local user in the loop; stop observing input.
  stopActivityTracking(session);
  await updateControllerBadge(session.client, 'cloud');
  json(res, 200, { ok: true, data: await toSessionInfo(session) } satisfies DaemonResponse<SessionInfo>);
}

async function handleAcquireCloud(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, { ok: false, error: `Session "${sessionId}" not found` } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'Cloud lock operations are not supported in attached mode.',
    } satisfies DaemonResponse);
    return;
  }

  touch(session);
  session.lock = {
    controller: 'cloud',
    acquiredAt: new Date().toISOString(),
    cloudPaused: false,
  };

  // Cloud is driving now — no local user in the loop; stop observing input.
  stopActivityTracking(session);
  await updateControllerBadge(session.client, 'cloud');
  json(res, 200, { ok: true, data: await toSessionInfo(session) } satisfies DaemonResponse<SessionInfo>);
}

async function handleReleaseCloud(sessionId: string, res: ServerResponse) {
  const session = sessions.get(sessionId);
  if (!session) {
    json(res, 404, { ok: false, error: `Session "${sessionId}" not found` } satisfies DaemonResponse);
    return;
  }
  if (isAttached(session)) {
    json(res, 400, {
      ok: false,
      error: 'Cloud lock operations are not supported in attached mode.',
    } satisfies DaemonResponse);
    return;
  }

  touch(session);
  session.lock = {
    controller: 'cli',
    acquiredAt: new Date().toISOString(),
    cloudPaused: false,
  };

  // Back under CLI/agent control — re-arm cooperative user-activity observation.
  clearUserActivity(session);
  if (session.mode === 'headed') {
    startActivityTracking(session);
    await updateControllerBadge(session.client, 'cli', { interactive: true });
  } else {
    await updateControllerBadge(session.client, 'cli');
  }
  json(res, 200, { ok: true, data: await toSessionInfo(session) } satisfies DaemonResponse<SessionInfo>);
}

/* ── Server routing ──────────────────────────────────────────────────── */

async function handleRequest(req: IncomingMessage, res: ServerResponse) {
  const method = req.method ?? 'GET';
  const { path: segments } = parseRoute(req.url ?? '/');
  const body = method === 'POST' || method === 'DELETE' ? await readBody(req) : '';

  try {
    // GET /health
    if (method === 'GET' && segments[0] === 'health') {
      await handleHealth(res);
      return;
    }

    // POST /sessions
    if (method === 'POST' && segments[0] === 'sessions' && segments.length === 1) {
      await handleCreateSession(body, res);
      return;
    }

    // GET /sessions
    if (method === 'GET' && segments[0] === 'sessions' && segments.length === 1) {
      await handleListSessions(res);
      return;
    }

    // GET /sessions/:id
    if (method === 'GET' && segments[0] === 'sessions' && segments.length === 2) {
      await handleGetSession(segments[1], res);
      return;
    }

    // DELETE /sessions (all)
    if (method === 'DELETE' && segments[0] === 'sessions' && segments.length === 1) {
      await handleDeleteAllSessions(res);
      return;
    }

    // DELETE /sessions/:id
    if (method === 'DELETE' && segments[0] === 'sessions' && segments.length === 2) {
      await handleDeleteSession(segments[1], res);
      return;
    }

    // POST /sessions/:id/swap-context
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'swap-context' &&
      segments.length === 3
    ) {
      await handleSwapContext(segments[1], body, res);
      return;
    }

    // GET /sessions/:id/storage-state
    if (
      method === 'GET' &&
      segments[0] === 'sessions' &&
      segments[2] === 'storage-state' &&
      segments.length === 3
    ) {
      await handleGetStorageState(segments[1], res);
      return;
    }

    // GET /sessions/:id/lock
    if (
      method === 'GET' &&
      segments[0] === 'sessions' &&
      segments[2] === 'lock' &&
      segments.length === 3
    ) {
      await handleGetLock(segments[1], res);
      return;
    }

    // POST /sessions/:id/take-control
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'take-control' &&
      segments.length === 3
    ) {
      await handleTakeControl(segments[1], res);
      return;
    }

    // POST /sessions/:id/resume-cloud
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'resume-cloud' &&
      segments.length === 3
    ) {
      await handleResumeCloud(segments[1], res);
      return;
    }

    // POST /sessions/:id/acquire-cloud (internal — called by relay)
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'acquire-cloud' &&
      segments.length === 3
    ) {
      await handleAcquireCloud(segments[1], res);
      return;
    }

    // POST /sessions/:id/release-cloud (internal — called by relay)
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'release-cloud' &&
      segments.length === 3
    ) {
      await handleReleaseCloud(segments[1], res);
      return;
    }

    // POST /sessions/:id/tools/:toolName
    if (
      method === 'POST' &&
      segments[0] === 'sessions' &&
      segments[2] === 'tools' &&
      segments.length === 4
    ) {
      await handleToolCall(segments[1], segments[3], body, res);
      return;
    }

    json(res, 404, { ok: false, error: 'Not found' });
  } catch (err) {
    json(res, 500, { ok: false, error: err instanceof Error ? err.message : String(err) });
  }
}

/* ── Pidfile management ──────────────────────────────────────────────── */

async function writePidfile(port: number) {
  await fs.mkdir(PIDFILE_DIR, { recursive: true, mode: 0o700 });
  const state: DaemonState = {
    pid: process.pid,
    port,
    startedAt: new Date().toISOString(),
  };
  // Write-then-rename so a concurrent reader never sees a half-written pidfile.
  const tmp = `${PIDFILE_PATH}.${process.pid}.tmp`;
  await fs.writeFile(tmp, JSON.stringify(state, null, 2), { mode: 0o600 });
  await fs.rename(tmp, PIDFILE_PATH);
}

/**
 * Remove the pidfile, but only if it still points at us. Prevents a redundant
 * or exiting daemon from deleting the entry a *different*, live daemon owns —
 * which would orphan that daemon's sessions and trigger a needless respawn.
 */
async function removePidfile() {
  try {
    const state = JSON.parse(await fs.readFile(PIDFILE_PATH, 'utf-8')) as DaemonState;
    if (state.pid !== process.pid) return;
    await fs.unlink(PIDFILE_PATH);
  } catch {
    // Already removed, unreadable, or not ours.
  }
}

/** Synchronous, ownership-aware variant for the `process.on('exit')` path. */
function removePidfileIfOwnedSync() {
  try {
    const state = JSON.parse(readFileSync(PIDFILE_PATH, 'utf-8')) as DaemonState;
    if (state.pid === process.pid) unlinkSync(PIDFILE_PATH);
  } catch {
    // Already removed, unreadable, or not ours.
  }
}

async function readPidfileState(): Promise<DaemonState | null> {
  try {
    return JSON.parse(await fs.readFile(PIDFILE_PATH, 'utf-8')) as DaemonState;
  } catch {
    return null;
  }
}

function isProcessAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

async function daemonHealthCheck(port: number): Promise<boolean> {
  try {
    const res = await fetch(`http://127.0.0.1:${port}/health`, {
      signal: AbortSignal.timeout(2000),
      headers: { Connection: 'close' },
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * True when another healthy daemon already owns the pidfile. A redundantly
 * spawned daemon defers to it (singleton guard) instead of clobbering the
 * pidfile and stranding the incumbent's sessions.
 */
async function anotherDaemonOwnsPidfile(): Promise<DaemonState | null> {
  const state = await readPidfileState();
  if (!state || state.pid === process.pid) return null;
  if (isProcessAlive(state.pid) && (await daemonHealthCheck(state.port))) return state;
  return null;
}

/* ── Cleanup ─────────────────────────────────────────────────────────── */

async function cleanup() {
  if (idleSweepTimer) {
    clearInterval(idleSweepTimer);
    idleSweepTimer = null;
  }
  for (const session of sessions.values()) {
    await teardownSession(session);
  }
  sessions.clear();
  await removePidfile();
}

/* ── Main entry ──────────────────────────────────────────────────────── */

async function startDaemon(): Promise<{ port: number }> {
  const server = createServer(handleRequest);

  return new Promise((resolve, reject) => {
    server.listen(0, '127.0.0.1', async () => {
      const addr = server.address();
      if (!addr || typeof addr === 'string') {
        reject(new Error('Failed to get server address'));
        return;
      }

      const port = addr.port;
      await writePidfile(port);
      resetIdleTimer();

      idleSweepTimer = setInterval(() => {
        sweepIdleSessions().catch((err) => {
          consoleLogger.warn(`[daemon] idle sweep failed: ${err}`);
        });
      }, SESSION_IDLE_SWEEP_INTERVAL_MS);
      // Don't keep the event loop alive for the sweep alone.
      idleSweepTimer.unref?.();

      // Graceful shutdown
      process.on('SIGINT', async () => {
        await cleanup();
        process.exit(0);
      });
      process.on('SIGTERM', async () => {
        await cleanup();
        process.exit(0);
      });
      process.on('exit', removePidfileIfOwnedSync);

      resolve({ port });
    });

    server.on('error', reject);
  });
}

/** CLI entry point: `canary session daemon` (internal, spawned by daemon-client) */
export async function runDaemon() {
  // Redirect all console output to stderr before starting anything.
  // The spawner destroys our stdout pipe after reading DAEMON_READY,
  // so any subsequent stdout writes (e.g. console.info/debug from
  // browser-core's consoleLogger) would crash the process with EPIPE.
  const { Console } = await import('node:console');
  global.console = new Console({ stdout: process.stderr, stderr: process.stderr }) as typeof console;

  // Singleton guard: if a healthy daemon already owns the pidfile, defer to it
  // and report its port instead of starting a competitor. This is the last line
  // of defense against the multi-daemon pidfile race — the client-side spawn
  // lock should make redundant spawns rare, but an external/racing spawn (e.g.
  // a different CLI build) must never clobber a live daemon's pidfile.
  const incumbent = await anotherDaemonOwnsPidfile();
  if (incumbent) {
    process.stdout.write(`DAEMON_READY:${incumbent.port}\n`);
    process.exit(0);
  }

  const { port } = await startDaemon();
  process.stdout.write(`DAEMON_READY:${port}\n`);
}
